import React from 'react';
import { Box, Text, Flex, Icon } from '@chakra-ui/react';

/**
 * Composant Badge pour afficher des statistiques de manière élégante
 * Inspiré des badges de récompense avec design circulaire
 */
export const MemberBadge = ({ 
  icon, 
  label, 
  value, 
  color = "blue",
  variant = "solid" // solid, outline, gradient
}) => {
  const colorSchemes = {
    blue: { bg: 'blue.50', color: 'blue.600', border: 'blue.300', gradient: 'linear(to-br, blue.400, blue.600)' },
    green: { bg: 'green.50', color: 'green.600', border: 'green.300', gradient: 'linear(to-br, green.400, green.600)' },
    purple: { bg: 'purple.50', color: 'purple.600', border: 'purple.300', gradient: 'linear(to-br, purple.400, purple.600)' },
    orange: { bg: 'orange.50', color: 'orange.600', border: 'orange.300', gradient: 'linear(to-br, orange.400, orange.600)' },
    red: { bg: 'red.50', color: 'red.600', border: 'red.300', gradient: 'linear(to-br, red.400, red.600)' },
    teal: { bg: 'teal.50', color: 'teal.600', border: 'teal.300', gradient: 'linear(to-br, teal.400, teal.600)' }
  };

  const scheme = colorSchemes[color] || colorSchemes.blue;

  if (variant === "gradient") {
    return (
      <Box
        position="relative"
        p={3}
        borderRadius="lg"
        bgGradient={scheme.gradient}
        color="white"
        shadow="md"
        transition="all 0.3s"
        _hover={{ transform: 'scale(1.05)', shadow: 'lg' }}
        overflow="hidden"
      >
        {/* Effet de brillance */}
        <Box
          position="absolute"
          top="-50%"
          right="-50%"
          width="100%"
          height="100%"
          bg="white"
          opacity="0.1"
          borderRadius="full"
        />
        
        <Flex direction="column" align="center" justify="center" position="relative">
          {icon && (
            <Box
              bg="whiteAlpha.300"
              p={2}
              borderRadius="full"
              mb={2}
              backdropFilter="blur(10px)"
            >
              <Icon as={icon} boxSize={5} />
            </Box>
          )}
          
          <Text fontSize="2xl" fontWeight="extrabold" mb={1}>
            {value}
          </Text>
          
          <Text fontSize="xs" fontWeight="medium" textTransform="uppercase" letterSpacing="wide">
            {label}
          </Text>
        </Flex>
      </Box>
    );
  }

  if (variant === "outline") {
    return (
      <Box
        p={3}
        borderRadius="lg"
        border="2px solid"
        borderColor={scheme.border}
        bg="white"
        shadow="sm"
        transition="all 0.3s"
        _hover={{ 
          transform: 'translateY(-2px)', 
          shadow: 'md',
          borderColor: scheme.color
        }}
      >
        <Flex direction="column" align="center" justify="center">
          {icon && (
            <Box
              bg={scheme.bg}
              p={2}
              borderRadius="full"
              mb={2}
            >
              <Icon as={icon} boxSize={5} color={scheme.color} />
            </Box>
          )}
          
          <Text fontSize="2xl" fontWeight="extrabold" color={scheme.color} mb={1}>
            {value}
          </Text>
          
          <Text fontSize="xs" fontWeight="semibold" color="gray.600" textTransform="uppercase">
            {label}
          </Text>
        </Flex>
      </Box>
    );
  }

  // Variant "solid" (default)
  return (
    <Box
      p={3}
      borderRadius="lg"
      bg="white"
      shadow="md"
      borderTop="3px solid"
      borderColor={scheme.color}
      transition="all 0.3s"
      _hover={{ 
        transform: 'translateY(-2px)', 
        shadow: 'lg'
      }}
    >
      <Flex direction="column" align="center" justify="center">
        {icon && (
          <Box
            bg={scheme.bg}
            p={2}
            borderRadius="full"
            mb={2}
          >
            <Icon as={icon} boxSize={5} color={scheme.color} />
          </Box>
        )}
        
        <Text fontSize="2xl" fontWeight="extrabold" color={scheme.color} mb={1}>
          {value}
        </Text>
        
        <Text fontSize="xs" fontWeight="semibold" color="gray.600" textAlign="center">
          {label}
        </Text>
      </Flex>
    </Box>
  );
};

/**
 * Composant BadgeRibbon pour un design en forme de ruban/rosette
 */
export const BadgeRibbon = ({ icon, label, value, color = "blue" }) => {
  const colorSchemes = {
    blue: { main: 'blue.500', light: 'blue.100', dark: 'blue.700' },
    green: { main: 'green.500', light: 'green.100', dark: 'green.700' },
    purple: { main: 'purple.500', light: 'purple.100', dark: 'purple.700' },
    orange: { main: 'orange.500', light: 'orange.100', dark: 'orange.700' },
  };

  const scheme = colorSchemes[color] || colorSchemes.blue;

  return (
    <Flex direction="column" align="center" justify="center" p={2}>
      {/* Badge circulaire */}
      <Box
        position="relative"
        bg={scheme.light}
        borderRadius="full"
        p={4}
        shadow="md"
        border="3px solid"
        borderColor={scheme.main}
        mb={-2}
        zIndex={2}
        transition="all 0.3s"
        _hover={{ transform: 'rotate(15deg) scale(1.1)' }}
      >
        <Flex direction="column" align="center">
          {icon && (
            <Icon as={icon} boxSize={4} color={scheme.dark} mb={1} />
          )}
          <Text fontSize="xl" fontWeight="black" color={scheme.dark}>
            {value}
          </Text>
        </Flex>
      </Box>

      {/* Rubans */}
      <Flex gap={1} mt={1}>
        <Box
          bg={scheme.main}
          w="20px"
          h="30px"
          clipPath="polygon(0 0, 100% 0, 100% 80%, 50% 100%, 0 80%)"
          shadow="sm"
        />
        <Box
          bg={scheme.dark}
          w="20px"
          h="30px"
          clipPath="polygon(0 0, 100% 0, 100% 80%, 50% 100%, 0 80%)"
          shadow="sm"
        />
      </Flex>

      <Text 
        fontSize="xs"
        fontWeight="bold" 
        color="gray.700" 
        mt={1}
        textTransform="uppercase"
        letterSpacing="wide"
      >
        {label}
      </Text>
    </Flex>
  );
};

export default MemberBadge;